import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  UserCog,
  Settings,
  FileSpreadsheet,
  History,
  LogOut,
  Sun,
  Moon,
  Kanban,
  X,
  Megaphone,
  Package,
  Copy,
  Mail,
  Zap,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import { Button } from "../ui/button";
import { cn, getInitials } from "../../lib/utils";
import { Avatar, AvatarFallback } from "../ui/avatar";
import { Separator } from "../ui/separator";

export const Sidebar = ({ isOpen, onClose }) => {
  const { user, logout, isAdmin } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const navItems = [
    { to: "/", icon: LayoutDashboard, label: "Tableau de bord", roles: ["ADMIN", "SELLER"] },
    { to: "/leads", icon: Users, label: "Leads", roles: ["ADMIN", "SELLER"] },
    { to: "/kanban", icon: Kanban, label: "Pipeline", roles: ["ADMIN", "SELLER"] },
    { to: "/import", icon: FileSpreadsheet, label: "Import CSV", roles: ["ADMIN"] },
    { to: "/campaigns", icon: Megaphone, label: "Campagnes", roles: ["ADMIN"] },
    { to: "/sources", icon: Package, label: "Sources", roles: ["ADMIN"] },
    { to: "/duplicates", icon: Copy, label: "Doublons", roles: ["ADMIN"] },
    { to: "/sellers", icon: UserCog, label: "Vendeurs", roles: ["ADMIN"] },
    { to: "/email-settings", icon: Mail, label: "Email", roles: ["ADMIN", "SELLER"] },
    { to: "/leadflow", icon: Zap, label: "LeadFlow", roles: ["ADMIN"] },
    { to: "/settings", icon: Settings, label: "Paramètres", roles: ["ADMIN"] },
    { to: "/audit", icon: History, label: "Historique", roles: ["ADMIN"] },
  ];

  const filteredItems = navItems.filter((item) =>
    item.roles.includes(user?.role)
  );

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        data-testid="sidebar"
        className={cn(
          "fixed lg:static inset-y-0 left-0 z-50 w-64 bg-card border-r border-border flex flex-col transform transition-transform duration-200 ease-in-out lg:transform-none",
          isOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        )}
      >
        {/* Header */}
        <div className="p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
              <span className="text-primary-foreground font-bold text-lg">LM</span>
            </div>
            <div>
              <h1 className="font-bold text-lg tracking-tight">LeadMaster</h1>
              <p className="text-xs text-muted-foreground uppercase tracking-widest">CRM</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={onClose}
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        <Separator />

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {filteredItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={onClose}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors",
                  isActive
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                )
              }
              data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
            >
              <item.icon className="h-5 w-5" />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <Separator />

        {/* Footer */}
        <div className="p-4 space-y-4">
          {/* Theme toggle */}
          <Button
            variant="outline"
            className="w-full justify-start gap-3"
            onClick={toggleTheme}
            data-testid="theme-toggle"
          >
            {theme === "light" ? (
              <>
                <Moon className="h-4 w-4" />
                Mode sombre
              </>
            ) : (
              <>
                <Sun className="h-4 w-4" />
                Mode clair
              </>
            )}
          </Button>

          {/* User info */}
          <div className="flex items-center gap-3 p-3 rounded-lg bg-secondary/50">
            <Avatar className="h-10 w-10">
              <AvatarFallback className="bg-primary text-primary-foreground text-sm font-semibold">
                {getInitials(user?.name)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.name}</p>
              <p className="text-xs text-muted-foreground">
                {isAdmin ? "Administrateur" : "Vendeur"}
              </p>
            </div>
          </div>

          {/* Logout */}
          <Button
            variant="ghost"
            className="w-full justify-start gap-3 text-destructive hover:text-destructive hover:bg-destructive/10"
            onClick={handleLogout}
            data-testid="logout-button"
          >
            <LogOut className="h-4 w-4" />
            Déconnexion
          </Button>
        </div>
      </aside>
    </>
  );
};
